/**
 * Conector MCP: las llaves para que un bot externo trabaje con la clínica
 * (dirección, 2026-09-15; abierto a escribir el 21/9/2026).
 *
 * Grok entraba abriendo el navegador y pinchando por la pantalla. Con una de
 * estas llaves entra directo. Nace de solo consultar; que además cambie cosas
 * se marca a propósito. Desde el 26/10/2026 escribe la llave de cualquier
 * persona del personal, con los permisos de su dueño (backend:
 * services/mcp_catalogo); hasta entonces, solo las de dirección.
 *
 * Las mejoras del software (20/10/2026) van en un tipo de llave aparte,
 * «Solo mejoras software», la de la rutina: alcanza las dos herramientas de
 * las mejoras (`mejoras_pendientes` y `actualizar_mejora`) y nada más (ni
 * pacientes, ni citas, ni ninguna otra), y no se le cambian los permisos.
 * Ninguna otra llave las alcanza. La crea quien tiene la casilla «Mejoras
 * software», a nombre de alguien de dirección que también la tenga (lo
 * comprueba el servidor; aquí solo se avisa antes).
 *
 * La llave va atada a una persona, así que cada bot ve exactamente lo que ve
 * esa persona dentro del programa. Se enseña UNA vez al crearla y no se vuelve
 * a poder leer: si se pierde, se revoca y se hace otra.
 */
import React, { useEffect, useState } from 'react';
import { api, getStoredUser, mensajeError } from '../lib/api';
import { SectionCard } from './Common';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { isManagement, normalizeRole, tieneMejorasSoftware } from '../lib/roles';
import { KeyRound, Copy, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const URL_CONECTOR = `${window.location.origin}/api/mcp`;

function cuando(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

/**
 * Lo que la llave hace DE VERDAD, que no es lo mismo que la casilla (segunda
 * pasada del supervisor, 23/9/2026). `escribe` y `entra` los calcula el
 * servidor con la misma regla que aplica el conector (services/mcp_tokens.
 * _ficha): aquí no se decide nada, solo se dice. Desde el 26/10/2026 la
 * llave de cualquier persona del personal con la casilla escribe, con sus
 * permisos.
 */
function queHace(l) {
  if (l.bot_whatsapp) {
    // La llave del bot de WhatsApp (1/11/2026): solo contestar en la bandeja.
    if (!l.activo) return 'era del bot de WhatsApp';
    if (l.entra === false) return 'no entra: su dueño ya no puede usarla';
    return l.contesta_whatsapp ? 'solo el bot de WhatsApp'
      : 'solo el bot de WhatsApp (no alcanza nada: su dueño no usa la bandeja de WhatsApp)';
  }
  if (l.solo_mejoras) {
    if (!l.activo) return 'era solo para Mejoras software';
    if (l.entra === false) return 'no entra: su dueño ya no puede usarla';
    return l.lee_mejoras ? 'solo Mejoras software'
      : 'solo Mejoras software (no alcanza nada: su dueño no es de dirección o no tiene la casilla)';
  }
  if (!l.activo) return l.escritura ? 'tenía marcada la escritura' : 'era de solo consultar';
  if (l.entra === false) return 'no entra: su dueño ya no puede usarla';
  if (l.escribe) return 'consultar y cambiar';
  return 'solo consultar';
}

/**
 * Lo que una llave CON escritura podrá cambiar, según el rol de su dueño
 * (26/10/2026). Es lo que ese rol hace desde su pantalla, y nada más: el
 * servidor manda (cada ruta mira el rol, el autor, la agenda o si la ficha es
 * suya). Aquí solo se dice en palabras de la clínica. Lo que no hace ninguna
 * llave que no sea de dirección —firmar por nadie, sacar enlaces para
 * pacientes— va al final.
 */
function queCambia(persona) {
  const nombre = persona?.name || persona?.user_nombre || 'su dueño';
  if (isManagement(persona)) {
    return `La de ${nombre} podrá crear, editar y borrar lo mismo que desde su pantalla.`;
  }
  const fin = ' Lo mismo que desde su pantalla y con sus permisos, nada más; no firmará '
    + 'por nadie ni sacará enlaces para pacientes.';
  const rol = normalizeRole(persona?.role || persona?.rol);
  if (rol === 'doctor') {
    return `La de ${nombre} podrá escribir en la historia y los evolutivos de sus `
      + 'pacientes, subir y marcar fotos, hacer recetas e informes y llevar las '
      + `citas de su agenda, solo con los pacientes cuya ficha puede abrir.${fin}`;
  }
  if (rol === 'anestesista') {
    // Lo de su pantalla (Quirófano, Anestesia, Registro QX y Recetas), y nada
    // de la Agenda: ni dar, mover o anular citas, ni el lugar o el cirujano
    // del día (revisión de seguridad del 26/10/2026).
    return `La de ${nombre} podrá rellenar las hojas de anestesia, el registro y los `
      + 'partes de quirófano, poner los tiempos y el estado del paciente en el parte, '
      + 'elegir el anestesista del día, preparar consentimientos y postoperatorios y '
      + 'hacer recetas e informes de los pacientes de quirófano; no dará, moverá ni '
      + `anulará citas.${fin}`;
  }
  if (rol === 'enfermeria') {
    return `La de ${nombre} podrá trabajar con pacientes, citas, sus evolutivos y el `
      + `registro de quirófano, y subir y marcar fotos.${fin}`;
  }
  return `La de ${nombre} podrá trabajar con citas, pacientes, presupuestos, cobros y `
    + `documentos.${fin}`;
}

async function copiar(texto) {
  try {
    await navigator.clipboard.writeText(texto);
    toast.success('Copiado');
  } catch {
    toast.error('No se ha podido copiar: selecciónalo y cópialo a mano');
  }
}

export default function ConectorMcpPanel() {
  const usuario = getStoredUser();
  const [llaves, setLlaves] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [nombre, setNombre] = useState('');
  // Que la llave además CAMBIE cosas hay que marcarlo a propósito: nace de
  // mirar (dirección, 21/9/2026, al abrir el conector a todo el producto).
  const [escritura, setEscritura] = useState(false);
  // La llave de la rutina (20/10/2026): solo las mejoras, nada más. Solo se
  // ofrece a quien tiene la casilla «Mejoras software».
  const [soloMejoras, setSoloMejoras] = useState(false);
  const ofreceSoloMejoras = tieneMejorasSoftware(usuario);
  // La llave del bot de WhatsApp (1/11/2026): solo sus tres herramientas.
  const [botWhatsApp, setBotWhatsApp] = useState(false);
  const [deQuien, setDeQuien] = useState('');
  const [creando, setCreando] = useState(false);
  const [reciente, setReciente] = useState(null);   // la única vez que se ve

  const puede = isManagement(usuario);

  const cargar = async () => {
    try {
      const r = await api.get('/api/mcp/tokens');
      setLlaves(r.data?.tokens || []);
    } catch (e) { toast.error(mensajeError(e, 'No se han podido leer las llaves')); }
  };

  useEffect(() => {
    if (!puede) return;
    cargar();
    // Los kioscos de tablet no pueden tener llave: el backend los rechaza y
    // aquí ni se ofrecen, para no proponer algo que va a fallar.
    api.get('/api/users').then(r => {
      const activos = (r.data || []).filter(
        u => u.active !== false && !String(u.role || '').startsWith('tablet'));
      setPersonas(activos);
      setDeQuien(d => d || usuario?.id || (activos[0] || {}).id || '');
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puede]);

  if (!puede) return null;

  // A nombre de quién se va a crear: para decir, al marcar la casilla, lo que
  // esa llave podrá cambiar con los permisos de esa persona.
  const dueno = personas.find(p => p.id === deQuien);

  const crear = async () => {
    if (!nombre.trim()) { toast.error('Ponle un nombre al conector'); return; }
    setCreando(true);
    try {
      let cuerpo = { nombre: nombre.trim(), user_id: deQuien, escritura };
      if (soloMejoras) cuerpo = { nombre: nombre.trim(), user_id: deQuien, escritura: false, solo_mejoras: true };
      else if (botWhatsApp) cuerpo = { nombre: nombre.trim(), user_id: deQuien, escritura: false, bot_whatsapp: true };
      const r = await api.post('/api/mcp/tokens', cuerpo);
      setReciente(r.data?.token || '');
      setNombre('');
      setSoloMejoras(false);
      setBotWhatsApp(false);
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido crear la llave'));
    } finally { setCreando(false); }
  };

  // Encender o apagar la escritura de una llave que YA está en uso, sin tener
  // que crear otra y reconfigurar el bot (dirección, 21/9/2026). Sigue siendo a
  // propósito: nadie se encuentra su llave escribiendo sin marcarlo.
  const cambiarEscritura = async (llave) => {
    const dar = !llave.escritura;
    // Lo que la llave podrá cambiar, con los permisos de su dueño
    // (26/10/2026: antes, a la de alguien que no era de dirección se le decía
    // que se quedaba con las consultas de siempre).
    // Sin rol en la ficha (un servidor de antes), se fía de `de_direccion`.
    const duenoDeLaLlave = {
      name: llave.user_nombre,
      role: llave.rol || (llave.de_direccion ? 'direccion' : ''),
    };
    const pregunta = `¿Dejar que «${llave.nombre}» cambie cosas en la clínica?\n\n`
      + `${queCambia(duenoDeLaLlave)} Lo que no tiene vuelta atrás se lo seguiremos `
      + 'preguntando.';
    if (dar && !window.confirm(pregunta)) return;
    try {
      const r = await api.put(`/api/mcp/tokens/${llave.id}`, { escritura: dar });
      // Lo que se anuncia es lo que dice el servidor que hace la llave ahora.
      const escribe = !!r?.data?.ficha?.escribe;
      toast.success(!dar ? 'Esa llave vuelve a ser de solo consulta'
        : escribe ? 'Esa llave ya puede trabajar'
          : 'Marcado, pero la llave no entra: su dueño ya no puede usarla');
      await cargar();
    } catch (e) { toast.error(mensajeError(e, 'No se ha podido cambiar')); }
  };

  const revocar = async (llave) => {
    if (!window.confirm(`¿Revocar «${llave.nombre}»? El bot dejará de entrar ahora mismo.`)) return;
    try {
      await api.delete(`/api/mcp/tokens/${llave.id}`);
      toast.success('Llave revocada');
      await cargar();
    } catch (e) { toast.error(mensajeError(e, 'No se ha podido revocar')); }
  };

  return (
    <SectionCard title="Conector para bots" className="lg:col-span-2">
      <p className="text-sm text-muted-foreground mb-4">
        Sirve para que un asistente externo —por ejemplo Grok— trabaje con la
        clínica sin entrar por la pantalla. Cada llave va a nombre de una persona
        y trabaja <strong>con los permisos de su dueño</strong>: nunca alcanza más
        de lo que esa persona alcanza dentro del programa. La de un médico, sus
        pacientes; la de administración, lo de administración.
      </p>
      <p className="text-sm text-muted-foreground mb-4">
        Una llave nace <strong>de solo mirar</strong>. Si además marcas que pueda
        cambiar cosas, el bot podrá crear, editar y borrar lo mismo que esa
        persona desde su pantalla; lo que no tiene vuelta atrás (borrar algo,
        mandar un WhatsApp o un correo a una paciente) le exige confirmarlo
        expresamente. Por el conector no se tocan, para nadie, los usuarios ni la
        configuración de la clínica, y una llave que no es de dirección tampoco
        firma por nadie ni saca enlaces para pacientes.
      </p>
      <p className="text-xs text-muted-foreground mb-5">
        Por esta puerta salen datos de salud a un proveedor externo, y la llave es
        eso: una llave. Quien la tenga hace lo que haría su dueño, así que se da
        como se daría un juego de llaves de la consulta, y se revoca en cuanto
        sobre. Todo lo que hace queda apuntado en Control de rastreo.
      </p>

      <div className="rounded-md border bg-muted/30 p-3 mb-5 text-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground">Dirección del conector</div>
            <code className="break-all">{URL_CONECTOR}</code>
          </div>
          <Button variant="outline" size="sm" onClick={() => copiar(URL_CONECTOR)}
            data-testid="mcp-copiar-url"><Copy className="w-4 h-4" /></Button>
        </div>
      </div>

      {reciente && (
        <div className="rounded-md border border-[#2E6E5E] bg-[#2E6E5E]/5 p-4 mb-5"
          data-testid="mcp-token-nuevo">
          <div className="font-semibold text-sm mb-1">Copia la llave ahora</div>
          <p className="text-xs text-muted-foreground mb-2">
            No se vuelve a enseñar. Si se pierde, revócala y haz otra.
          </p>
          <div className="flex items-center gap-2 mb-3">
            <code className="text-xs break-all flex-1">{reciente}</code>
            <Button variant="outline" size="sm" onClick={() => copiar(reciente)}
              data-testid="mcp-copiar-token"><Copy className="w-4 h-4" /></Button>
          </div>
          <div className="text-xs text-muted-foreground">
            En Grok: <strong>Conectores → Nuevo → Personalizado</strong>. Pega arriba la
            dirección del conector y añade la cabecera{' '}
            <code>Authorization: Bearer {'<la llave>'}</code>.
          </div>
          <Button variant="ghost" size="sm" className="mt-2" onClick={() => setReciente(null)}
            data-testid="mcp-ocultar-token">Ya la he copiado</Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-3 items-end mb-5">
        <div>
          <Label>Nombre del conector</Label>
          <Input value={nombre} onChange={e => setNombre(e.target.value)}
            placeholder="Grok de dirección" data-testid="mcp-nombre" />
        </div>
        <div>
          <Label>A nombre de</Label>
          <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={deQuien} onChange={e => setDeQuien(e.target.value)} data-testid="mcp-usuario">
            {personas.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <Button onClick={crear} disabled={creando}
          className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="mcp-crear">
          {creando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
          Crear llave
        </Button>
      </div>

      {ofreceSoloMejoras && (
      <label className="flex items-start gap-2 text-sm mb-5 cursor-pointer rounded-md border border-[#2E6E5E]/30 bg-[#2E6E5E]/5 p-3">
        <input type="checkbox" checked={soloMejoras} className="mt-1"
          onChange={e => setSoloMejoras(e.target.checked)} data-testid="mcp-solo-mejoras" />
        <span>
          <strong>Solo Mejoras software</strong> (la recomendada para la rutina
          automática de las mejoras): leer las mejoras pendientes con sus capturas y
          marcarlas como en curso, hechas, por aclarar o pendientes de dirección.
          <strong> Nada más</strong>: ni pacientes, ni citas, ni ninguna otra consulta.
          {soloMejoras && dueno && !(isManagement(dueno) && tieneMejorasSoftware(dueno)) && (
            <span className="block text-xs text-muted-foreground mt-1"
              data-testid="mcp-aviso-solo-mejoras">
              Tiene que ir a nombre de alguien de dirección con la casilla «Mejoras
              software»: la de {dueno.name} no se va a poder crear.
            </span>
          )}
        </span>
      </label>
      )}

      {!soloMejoras && (
      <label className="flex items-start gap-2 text-sm mb-5 cursor-pointer rounded-md border border-sky-300/60 bg-sky-50/60 p-3">
        <input type="checkbox" checked={botWhatsApp} className="mt-1"
          onChange={e => setBotWhatsApp(e.target.checked)} data-testid="mcp-bot-whatsapp" />
        <span>
          <strong>Bot de WhatsApp</strong> (para el bot que filtra los contactos): ver las
          conversaciones que lleva él y contestar en ellas; lo que escribe sale marcado
          «Bot» en WhatsApp. Nunca escribe primero, ni pasadas 24 h, ni en una conversación
          que lleva el equipo, y solo si en WhatsApp → Conexión está encendido «El bot
          puede contestar». <strong>Nada más</strong>: ni pacientes, ni citas, ni ninguna
          otra consulta.
        </span>
      </label>
      )}

      {!soloMejoras && !botWhatsApp && (
      <label className="flex items-start gap-2 text-sm mb-5 cursor-pointer">
        <input type="checkbox" checked={escritura} className="mt-1"
          onChange={e => setEscritura(e.target.checked)} data-testid="mcp-escritura" />
        <span>
          Que además pueda <strong>cambiar cosas</strong>, siempre con los permisos
          de su dueño: lo que esa persona puede cambiar desde su pantalla, y nada más.
          {escritura && (
            // Solo se dice: quien decide es el servidor, y la lista de abajo
            // pinta lo que la llave hace de verdad (`escribe`).
            <span className="block text-xs mt-1" data-testid="mcp-aviso-escritura">
              {dueno && (
                <span className="block text-muted-foreground" data-testid="mcp-que-cambia">
                  {queCambia(dueno)}
                </span>
              )}
              <span className="block text-amber-700">
                Esta llave trabajará en la clínica de verdad. Dásela solo a un bot
                en el que confíes, y revócala en cuanto no haga falta.
              </span>
            </span>
          )}
        </span>
      </label>
      )}

      {llaves.length === 0 ? (
        <p className="text-sm text-muted-foreground">Todavía no hay ninguna llave.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b">
                <th className="py-2 pr-3">Conector</th>
                <th className="py-2 pr-3">A nombre de</th>
                <th className="py-2 pr-3">Creada</th>
                <th className="py-2 pr-3">Último uso</th>
                <th className="py-2 pr-3 text-right">Consultas</th>
                <th className="py-2 pr-3">Puede</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {llaves.map(l => (
                <tr key={l.id} className={`border-b last:border-0 ${l.activo ? '' : 'text-muted-foreground'}`}
                  data-testid={`mcp-llave-${l.id}`}>
                  <td className="py-2 pr-3">
                    {l.nombre}
                    {!l.activo && <span className="ml-2 text-xs">(revocada)</span>}
                  </td>
                  <td className="py-2 pr-3">{l.user_nombre || '—'}</td>
                  <td className="py-2 pr-3">{cuando(l.creado_at)}</td>
                  <td className="py-2 pr-3">{cuando(l.ultimo_uso_at)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{l.usos}</td>
                  <td className="py-2 pr-3">
                    {l.solo_mejoras || l.bot_whatsapp ? (
                      // La llave de la rutina no se toca: si hace falta otra
                      // cosa, se revoca y se crea otra.
                      <span className={`text-xs px-2 py-1 rounded border ${(l.lee_mejoras || l.contesta_whatsapp) && l.activo
                        ? 'border-[#2E6E5E]/40 bg-[#2E6E5E]/5 text-[#2E6E5E]'
                        : 'border-border text-muted-foreground'}`}
                        data-testid={l.bot_whatsapp ? `mcp-bot-whatsapp-${l.id}` : `mcp-solo-mejoras-${l.id}`}>
                        {queHace(l)}
                      </span>
                    ) : l.activo ? (
                      <button onClick={() => cambiarEscritura(l)}
                        data-testid={`mcp-escritura-${l.id}`}
                        className={`text-xs px-2 py-1 rounded border ${l.escribe
                          ? 'border-amber-300 bg-amber-50 text-amber-800'
                          : 'border-border text-muted-foreground'}`}>
                        {queHace(l)}
                      </button>
                    ) : (
                      <span className="text-xs">{queHace(l)}</span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {l.activo && (
                      <Button variant="ghost" size="sm" onClick={() => revocar(l)}
                        data-testid={`mcp-revocar-${l.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}
